import React, { useState, useEffect } from 'react';
import { stepsData } from '../../data/mock';
import {
  ClipboardCheck,
  CreditCard,
  Calendar,
  Phone,
  FileCheck,
  Percent,
  Zap,
  Users,
  LayoutDashboard,
  Send,
  Check
} from 'lucide-react';

const iconMap = {
  ClipboardCheck,
  CreditCard,
  Calendar,
  Phone,
  FileCheck,
  Percent,
  Zap,
  Users,
  LayoutDashboard,
  Send
};

const PathStepsStrip = () => {
  const [activeStep, setActiveStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);

  // Check localStorage for completed steps based on user progress
  useEffect(() => {
    const checkProgress = () => {
      const completed = [];
      
      const savedProgress = localStorage.getItem('oneforce_preapproval_progress');
      if (savedProgress) {
        completed.push(1);
      }
      
      const applicationFeePaid = localStorage.getItem('applicationFeePaid') === 'true';
      if (applicationFeePaid) {
        completed.push(1, 2);
      }
      
      const fundingFeePaid = localStorage.getItem('fundingFeePaid') === 'true';
      if (fundingFeePaid) {
        completed.push(1, 2, 3, 4, 5, 6);
      }
      
      setCompletedSteps([...new Set(completed)]);
    };
    
    checkProgress();
    window.addEventListener('storage', checkProgress);
    return () => window.removeEventListener('storage', checkProgress);
  }, []);

  const isStepCompleted = (stepId) => completedSteps.includes(stepId);

  return (
    <section className="py-10 bg-slate-900 border-y border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">
            Your 10-Step Path to $120,000 in Co-Op Funding and being AI Horse-Powered
          </h3>
          <p className="text-slate-400 text-sm">
            Click any step to explore
            {completedSteps.length > 0 && (
              <span className="text-green-400 ml-2">
                • {completedSteps.length} step{completedSteps.length > 1 ? 's' : ''} completed
              </span>
            )}
          </p>
        </div>

        {/* Desktop Steps - Horizontal Interactive */}
        <div className="hidden md:block">
          <div className="relative">
            {/* Background Connection Line */}
            <div className="absolute top-8 left-[5%] right-[5%] h-1 bg-slate-700 rounded-full" />
            
            {/* Animated Progress Line */}
            <div
              className="absolute top-8 left-[5%] h-1 rounded-full transition-all duration-500 ease-out"
              style={{ 
                width: `${((activeStep - 1) / 9) * 90}%`,
                background: completedSteps.length > 0 
                  ? 'linear-gradient(90deg, #22c55e, #f59e0b)' 
                  : 'linear-gradient(90deg, #f59e0b, #f59e0b)'
              }}
            />

            {/* Steps */}
            <div className="grid grid-cols-10 gap-2">
              {stepsData.map((step) => {
                const Icon = iconMap[step.icon];
                const isActive = step.id === activeStep;
                const isCompleted = isStepCompleted(step.id);
                const isPast = step.id < activeStep;

                return (
                  <div
                    key={step.id}
                    className="flex flex-col items-center cursor-pointer group"
                    onClick={() => setActiveStep(step.id)}
                  >
                    {/* Icon Circle */}
                    <div
                      className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300 ${
                        isCompleted
                          ? 'bg-green-500 shadow-lg shadow-green-500/40 scale-105'
                          : isActive
                          ? 'bg-emerald-500 shadow-lg shadow-emerald-500/40 scale-110 ring-4 ring-emerald-500/30'
                          : isPast
                          ? 'bg-emerald-500/30 border-2 border-emerald-500'
                          : 'bg-slate-800 border-2 border-slate-600 group-hover:border-emerald-500/50 group-hover:bg-slate-700'
                      }`}
                    >
                      {isCompleted ? (
                        <Check className="w-8 h-8 text-white" />
                      ) : (
                        <Icon
                          className={`w-6 h-6 transition-all duration-300 ${
                            isActive 
                              ? 'text-slate-950 scale-110' 
                              : isPast
                              ? 'text-emerald-400'
                              : 'text-slate-400 group-hover:text-emerald-400'
                          }`}
                        />
                      )}
                    </div>

                    {/* Step Number & Title */}
                    <div className="mt-3 text-center">
                      <span
                        className={`block text-xs font-bold transition-colors ${
                          isCompleted 
                            ? 'text-green-400' 
                            : isActive 
                            ? 'text-emerald-400' 
                            : isPast
                            ? 'text-emerald-400/70'
                            : 'text-slate-500 group-hover:text-slate-300'
                        }`}
                      >
                        {isCompleted ? '✓' : step.id}
                      </span>
                      <span
                        className={`block text-xs mt-0.5 transition-colors ${
                          isCompleted 
                            ? 'text-green-400' 
                            : isActive 
                            ? 'text-white font-medium' 
                            : isPast
                            ? 'text-slate-400'
                            : 'text-slate-500 group-hover:text-slate-300'
                        }`}
                      >
                        {step.shortTitle}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Mobile Steps - Scrollable Interactive */}
        <div className="md:hidden">
          <div className="flex overflow-x-auto pt-2 pb-4 -mx-4 px-4 space-x-3 scrollbar-hide">
            {stepsData.map((step) => {
              const Icon = iconMap[step.icon];
              const isActive = step.id === activeStep;
              const isCompleted = isStepCompleted(step.id);

              return (
                <button
                  key={step.id}
                  className={`flex-shrink-0 flex flex-col items-center p-3 rounded-xl transition-all ${
                    isCompleted
                      ? 'bg-green-500/20 border-2 border-green-500'
                      : isActive
                      ? 'bg-emerald-500/20 border-2 border-emerald-500 scale-105'
                      : 'bg-slate-800/50 border-2 border-slate-700 active:scale-95'
                  }`}
                  onClick={() => setActiveStep(step.id)}
                >
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                      isCompleted 
                        ? 'bg-green-500' 
                        : isActive 
                        ? 'bg-emerald-500 shadow-lg shadow-emerald-500/30' 
                        : 'bg-slate-700'
                    }`}
                  >
                    {isCompleted ? (
                      <Check className="w-6 h-6 text-white" />
                    ) : (
                      <Icon
                        className={`w-5 h-5 ${
                          isActive ? 'text-slate-950' : 'text-slate-400'
                        }`}
                      />
                    )}
                  </div>
                  <span
                    className={`mt-2 text-xs font-medium ${
                      isCompleted 
                        ? 'text-green-400' 
                        : isActive 
                        ? 'text-emerald-400' 
                        : 'text-slate-500'
                    }`}
                  >
                    {isCompleted ? '✓' : step.id}. {step.shortTitle}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Active Step Indicator - Simple text only, no popup */}
        <div className="mt-6 text-center">
          <p className="text-slate-400 text-sm">
            <span className={`font-semibold ${isStepCompleted(activeStep) ? 'text-green-400' : 'text-emerald-400'}`}>
              {isStepCompleted(activeStep) ? '✓ Completed: ' : `Step ${activeStep}: `}
            </span>
            <span className="text-white">{stepsData[activeStep - 1].title}</span>
          </p>
        </div>
      </div>
    </section>
  );
};

export default PathStepsStrip;
